// "VRS November deals" popup — pure logic.
//
// A one-time modal on the first page a visitor lands on, announcing the sale's
// limited time deal. Everything it shows comes from the `sale-campaign` doc
// (lib/saleCampaign): `popup` holds the copy and image; the deals are the
// running `limitedDeals` for the visitor's region, in doc order (the first one
// is the headline and sets the countdown), each with its price, the regular
// price and the % off. No signup: one button to the product.
//
// One localStorage value per campaign drives what the visitor sees:
//   (none)    first visit: the modal opens after `delayMs`
//   STATE_TAB dismissed the modal: a small tab stays on the left edge on every
//             page; clicking it opens the modal again
//   STATE_OFF closed the tab with its own ×, or clicked through to the deal:
//             nothing, ever
// A new campaign gets a new `popup.key`, so it can show again.
//
// Isomorphic + pure (no React, no window access except through the arguments):
// the component passes `nowMs`, the path and a storage object in.
import { getCampaignPhase, getDealGroups, PHASE_LIVE, PHASE_TEASER } from './saleCampaign'

export const STATE_NEW = 'new'
export const STATE_TAB = 'tab'
export const STATE_OFF = 'off'

const DEFAULT_DELAY_MS = 5000

const REGION_CURRENCY = { us: 'USD', eu: 'EUR', au: 'AUD', world: 'EUR' }

// Pages where a modal would get in the way of finishing an order or signing in.
const EXCLUDED_PATH_RE = /\/(checkout|cart|restore-cart|signup|login|account|payment|thank-you|order)(\/|$)/i

export const stateKeyOf = (popup) => `vrs_popup_${popup.key}`

export function isPathExcluded(pathname) {
  return EXCLUDED_PATH_RE.test(String(pathname || ''))
}

/**
 * What the popup announces right now, or null when there is nothing to show:
 * the campaign `popup` block and the deal sets for `region` (getDealGroups),
 * each with its own timer and its deals priced in backend shape (price null on
 * pages without a region). A set still to come is announced with a "starts in"
 * timer; a set that has ended, or whose deals are all sold out, drops out.
 */
export function getDealPopup(campaign, nowMs, region) {
  const popup = campaign && campaign.popup
  if (!popup || !popup.key) return null
  const phase = getCampaignPhase(campaign, nowMs)
  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) return null
  const currency = REGION_CURRENCY[region]
  const money = (v) => (currency && Number.isFinite(v) ? { priceCents: Math.round(v * 100), currency } : null)
  const priced = (d) => {
    const price = money(d.prices && d.prices[region])
    const was = money(d.regularPrices && d.regularPrices[region])
    const regular = price && was && was.priceCents > price.priceCents ? was : null
    return {
      itemType: d.itemType,
      title: d.shortTitle || d.title,
      price,
      regular,
      percentOff: regular ? Math.round((1 - price.priceCents / regular.priceCents) * 100) : null,
    }
  }
  const groups = getDealGroups(campaign, nowMs, region)
    .map((g) => ({ ...g, deals: g.deals.filter((d) => d.soldOut !== true).map(priced) }))
    .filter((g) => g.deals.length > 0)
  if (!groups.length) return null
  const live = phase === PHASE_LIVE && groups.some((g) => g.live)
  // Deals on sale now (all of them in the teaser): the lowest price feeds the side tab.
  const deals = groups.filter((g) => g.live || !live).flatMap((g) => g.deals)
  return {
    popup,
    live,
    groups,
    targetMs: groups[0].targetMs,
    deals,
    // the lowest deal price, for the side tab ("Pedals from $189")
    fromPrice: deals.reduce((min, d) => (d.price && (!min || d.price.priceCents < min.priceCents) ? d.price : min), null),
  }
}

function readState(storage, popup) {
  try {
    if (!storage) return STATE_OFF
    const v = storage.getItem(stateKeyOf(popup))
    if (v === null) return STATE_NEW
    return v === STATE_TAB ? STATE_TAB : STATE_OFF
  } catch (_e) {
    // Storage blocked (private mode, sandboxed iframe): a visitor we cannot
    // remember would get the modal on every page, so show nothing.
    return STATE_OFF
  }
}

export function saveState(storage, popup, state) {
  try {
    if (storage) storage.setItem(stateKeyOf(popup), state)
  } catch (_e) {
    // ignore: see readState
  }
}

export function clearState(storage, popup) {
  try {
    if (storage) storage.removeItem(stateKeyOf(popup))
  } catch (_e) {
    // ignore: see readState
  }
}

export const popupDelayMs = (popup) => (Number.isFinite(popup && popup.delayMs) ? popup.delayMs : DEFAULT_DELAY_MS)

/** What to render on this page view: STATE_NEW (schedule the modal), STATE_TAB or STATE_OFF. */
export function getPopupState({ campaign, nowMs, region, pathname, storage }) {
  const view = getDealPopup(campaign, nowMs, region)
  if (!view) return STATE_OFF
  if (isPathExcluded(pathname)) return STATE_OFF
  return readState(storage, view.popup)
}
